import React, { useContext, useMemo, useEffect } from 'react';
import { GridTable } from '@ctsu/ctsu-mui-grid';
import { ValidatorForm } from 'components/FormHelpers/FormInputs';
import { openConfirmation } from 'helpers/Confirmation';
import { openSnackbar } from 'helpers/Notifier';
import { GetFromCtxValue, GetUserFullName, getDecodedQueryParams, getSysDate, useGetPathProtocol } from 'helpers';
import { httpStatusEnum, TE } from 'appconstants';
import { AppContext, withAppContext } from '@ctsu/loader';
import { compose, withHandlers, withState } from 'recompose';
import _ from 'lodash';
import { RenderElement } from 'components/RenderHelpers';
import { FasSyncAlt } from 'assets/icons';
import atoms from 'ctsutheme/components/atoms';
import { connect, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getAdminInfoAction } from 'actions/protocols/admin';
import { bindLogger } from 'helpers/BindLogger';
import { getParticipation } from 'actions/protocols/participation';
import EditParticipation from './EditParticipation';
import { ParticipationColumns } from '../gridcolumns';
import { getAdminInfoSelector } from '../../../../reducers/protocols/admin/adminReducer';

const { Tooltip } = atoms;

let setEditingFlag;
const Participations = ({
  participationInfo,
  saveParticipationInfo,
  state,
  setState,
  onRowClicked,
  participationDataLoad,
  participationIndex,
  syncParticipation,
  CanUserEditParticipation
}) => {

  const dispatch = useDispatch();
  const adminInfo = useSelector(getAdminInfoSelector);
  const { protocolNumber } = useSelector(s => s.prtDetails.prtInfo);
  const context = useContext(AppContext);
  const { isStartSync } = adminInfo || {};

  const actions = useMemo(() => bindActionCreators({
    getAdminInfoAction
  }, dispatch), [dispatch]);

  useEffect(() => {
    if (protocolNumber) {
      bindLogger(actions.getAdminInfoAction, context)(protocolNumber);
    }
  }, [protocolNumber])

  const RenderEditRow = (row, setEditing) => {
    if (_.isEmpty(state) && !_.isEqual(row, state)) {
      setState({ ...row });
    }

    setEditingFlag = (flg) => {
      setEditing(flg);
      setState({});
    };
    return (
      <EditParticipation
        state={state || row}
        setEditing={setEditing}
        defaultRow={row}
        setState={setState}
      />
    );
  };

  const saveParticipant = () => {
    saveParticipationInfo({
      ...state,
      dataSource: 'RSS',
      esysStatus: state.esysStatus,
      hasRestrictions: state.hasRestrictions || false
    }, 'edit');
  };

  const inactivateParticipant = row => {

    saveParticipationInfo({
      ...row,
      esysStatus: 'Inactive',
      endDate: getSysDate()
    }, 'delete');
  };
  return (
    <ValidatorForm onSubmit={saveParticipant}>
      <GridTable
        tag={`${TE.ID}participation${TE.TABLE}`}
        title="Participation"
        exportTitle={`Participation-${useGetPathProtocol()}`}
        columns={ParticipationColumns}
        rows={participationInfo}
        user={GetUserFullName()}
        editable
        editMode={CanUserEditParticipation}
        allowAddRow
        filterable
        sortable
        enableToolbar
        filterState={{ esysStatus: 'Active' }}
        deletable
        toPdf={false}
        onDataLoad={participationDataLoad}
        onDelete={inactivateParticipant}
        renderEditRow={RenderEditRow}
        rowDeletable={(row) => (row.dataSource === 'RSS' && row.orgType !== 'CGRP' && CanUserEditParticipation)}
        rowEditable={(row) => (row.dataSource === 'RSS' && row.orgType !== 'CGRP' && CanUserEditParticipation) || isStartSync === 'Y'}
        onRowClicked={onRowClicked}
        renderCustomHeaderIcons={() => (
          <RenderElement access={CanUserEditParticipation === true && isStartSync === 'N'}>
            <Tooltip
              tooltipplacementtop="true"
              title="Synchronize Participation Information"
              aria-label="Synchronize Participation Information"
            >
              <button
                type="button"
                onClick={() => syncParticipation()}
                data-id="syncParticipation"
                name="syncParticipation"
                className="fn-btn"
              >
                <FasSyncAlt />
              </button>
            </Tooltip>
          </RenderElement>
        )}
        highlightRowObject={participationIndex}
        recordPerPage={10}
      />
    </ValidatorForm>
  );
};

const Participation = compose(
  withAppContext,
  withState('state', 'setState', {}),
  connect(null, {getParticipation}),
    withHandlers({
    saveParticipationInfo: props => (postBody, action) => {
      const {
        onSave,
        setState,
        context
      } = props;
      const confirmSave = () => {
        const payload = _.omit(postBody, ['restrictions']);
        onSave(payload).subscribe(resp => {
          if (resp.status === httpStatusEnum.SUCCESS) {
            openSnackbar({
              message: GetFromCtxValue(context, 'PROTOCOL_PARTICIPATION_SUCCESS'),
              variant: 'success',
              name: 'participation'
            });
            setState({});
            if (action === 'edit') {
              setEditingFlag(false);
            }
          }
        });
      };
      openConfirmation({
        message: (action === 'edit') ? GetFromCtxValue(context, 'PROTOCOL_PARTICIPATION_CONFIRM') : 'Do you want to delete Participation information?', // PROTOCOL_PARTICIPATION_DELETE_CONFIRM
        onAction: confirmSave,
        name: 'participation'
      });
    },
    syncParticipation: props => event => {
      const { syncParticipants, context, getParticipation: updateParticipation } = props;
      const postSync = () => true;
      syncParticipants().subscribe(resp => {
        if (resp.status === httpStatusEnum.SUCCESS) {
          // refresh the frid after sync
          const params = getDecodedQueryParams(window.location.pathname.split('/').pop());
          const protocolNumber = params.split('/').shift();
          bindLogger(updateParticipation, context)(protocolNumber);
          // notify user
          openConfirmation({
            message: 'Participation information is synchronized', // GetFromCtxValue(context, 'PROTOCOL_PARTICIPATION_SYNC_SUCCESS')
            onAction: postSync,
            name: 'post_sync',
            hideButton2: true
          });
        }
      });
    }
  })
)(Participations);

export default Participation;
